import moment from 'moment';

import { MOCK } from '../../configDebug';
import configLib from '../../configLib';
import { IndividualExtendedInfoData } from '../models/enroll';
import { clone } from '../util/collectionHelpers';
import { TypedObject } from '../util/types';

import allMessages from './ams/getAllMessages.json';
import message from './ams/getMessage.json';
import summary from './ams/getSummary.json';
import notifyUnreadCount from './ams/notifyUnreadCount.json';
import voidResponse from './ams/voidResponse.json';
import userPermissions from './auth/userPermissions.json';
import country from './crm/country.json';
import individualExtendedInfoNew from './crm/individualExtendedInfo_new.json';
import individualExtendedInfo3 from './crm/individualExtendedInfo_onboarding-1.1-pd.json';
import individualExtendedInfo4 from './crm/individualExtendedInfo_onboarding-1.2-pd.json';
import individualExtendedInfo5 from './crm/individualExtendedInfo_onboarding-2.1-fin-qs.json';
import individualExtendedInfo6 from './crm/individualExtendedInfo_onboarding-2.4-fin-qs.json';
import individualExtendedInfo1 from './crm/individualExtendedInfo_onboarding-3.5-fin-qs.json';
import individualExtendedInfo7 from './crm/individualExtendedInfo_onboarding-pending-verification.json';
import individualExtendedInfo2 from './crm/individualExtendedInfo_onboarding-verification.json';
import linkedAccounts from './crm/individualLinkedAccounts.json';
import getPayments from './crm/payments.json';
import companies from './fundamental/companies.json';
import companyLogo from './fundamental/companyLogo.json';
import companyLogoAndName from './fundamental/companyLogoAndName.json';
import companyLogoMD5 from './fundamental/companyLogoMD5.json';
import earnings from './fundamental/earnings.json';
import popularStocks from './fundamental/popularStocks.json';
import search from './fundamental/search.json';
import sector from './fundamental/sector.json';
import sectors from './fundamental/sectors.json';
import stats from './fundamental/stats.json';
import cnbbo from './gateway/md/compressed-2.json';
import instrumentSnapshot from './gateway/md/instrument-snapshot.json';
import nbbo from './gateway/md/nbbo.json';
import ohlcv from './gateway/md/ohlcv-RecentPrices.json';
import trade from './gateway/md/trade.json';
import newsForSymbol from './gateway/news/news-for-symbols.json';
import newOrderAccepted from './gateway/sor/new-order-accepted.json';
import newOrderRejected from './gateway/sor/new-order-rejected.json';
import marketDataNbbo from './market-data/nbbo.json';
import marketDataOhlcv from './market-data/ohlcv.json';
import newsData from './news/news.json';
import accountTradeHistory from './reporting/accountTradeHistory.json';
import openPositionsData from './reporting/openPositionsData.json';
import orderDetail from './reporting/orderDetail.json';
import balances from './trading/balances.json';
import newOrder from './trading/neworder.json';
import orders from './trading/orders.json';
import positions from './trading/positions.json';
import badRequestData from './badRequest.json';
import emptyData from './empty.json';
import { ExtendedInfoMockEnum } from './types';

export const badRequest = badRequestData;
export const empty = emptyData;


// Auth
const authImplementation: any = {
  authorize: { rawState: {}, profile: {} },
  // common
  registerUser: { data: { id: 198 } },
  userPermissions,
  confirmEmail: { data: {} },
};
const authProxy = {
  get(target: any, callName: string, receiver: any) {
    if (!authImplementation[callName]) return empty;

    return authImplementation[callName];
  },
};
export const auth = new Proxy(authImplementation, authProxy);

// CRM
const crmImplementation: any = {
  // enroll
  country,
  getPayments,
  // enroll - other partially or fully filled examples
  linkedAccounts,
  // complex sets
  ...(individualExtendedInfo(MOCK.EXTENDED_INFO)),
};
type MockedCall = { id: number, count: number };
let callsRegistry: TypedObject<MockedCall> = {};
const crmProxy = {
  get(target: any, callName: string, receiver: any) {
    if ([ '$$typeof', 'prototype' ].includes(callName)) return target[callName];

    if (crmImplementation[callName] === undefined && MOCK.RESPONSE_EMPTY_FALLBACK) {
      console.warn(`[CRM-MOCK] No implementation for '${callName}' - returning empty response`);
      return empty;
    }

    // initial value of callsRegistry for callName
    let isInitial = !callsRegistry[callName];
    if (isInitial) {
      callsRegistry[callName] = { id: 1, count: 1 };
      isInitial = true;
    }
    let current = callsRegistry[callName];

    // update id and count (if not initial)
    if (!isInitial) {
      if (crmImplementation[`${callName}_${current.id + 1}`]) current.id++;
      current.count++;
    }

    // logging
    const { count, id } = current;
    if (id > 1 && crmImplementation[`${callName}_${id}`]) {
      console.debug(`[CRM-MOCK] Calling '${`${callName}_${id}`}' - ${count} time(s) called`);
      return crmImplementation[`${callName}_${id}`];
    }

    console.debug(`[CRM-MOCK] Calling '${callName}' for the first time`);
    return crmImplementation[callName];
  },
};
export const crm = new Proxy(crmImplementation, crmProxy);

// File
export const file: any = {
  upload: 'some-id',
};

// Gateway
export const gateway: any = {
  cnbbo,
  nbbo,
  ohlcv,
  news: newsForSymbol,
  newsForSymbol,
  trade,
  // TODO: This case has not yet been received for NewOrderRequest - temporarily using ParentOrder
  newOrderAccepted,
  newOrderRejected,
  instrumentSnapshot,
};

// Trading
export const trading: any = {
  getOrders: orders,
  getPositions: positions,
  newOrder,
  getBalances: balances,
};

// Market Data
const marketImpl: any = {
  subscribe: marketDataNbbo,
  nbbo: marketDataNbbo,
  ohlcv: marketDataOhlcv,
};
const marketProxy = {
  get(target: any, callName: string, receiver: any) {
    if (callName === 'ohlcv') {
      let tmp = clone(target[callName] ?? []);
      const item0 = clone(tmp[0]);
      if (item0) {
        item0.date = moment().format('YYYY-MM-DD'); // add current date for price info
        tmp = tmp.concat();
        tmp.push(item0);
      }
      return tmp;
    }
    return target[callName];
  },
};
export const market = new Proxy(marketImpl, marketProxy);

// Fundamental
export const fundamental: any = {
  search,
  company: companies,
  // TODO: Delete after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
  companyLogo,
  companyLogoAndName,
  companyLogoMD5,
  earnings,
  stats,
  popularStocks,
  sectors,
  sector,
};

// News
export const news: any = {
  news: newsData,
  newsForSymbol: newsData,
};

// Reporting
const openPositionsDataByAccount = {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  accounts: (openPositionsData as any[]).reduce((accounts: any[], position: any) => {
    const { accountReferenceId, ...rest } = position;
    let account = accounts.find(item => item.accountReferenceId === accountReferenceId);
    if (!account) {
      account = { accountReferenceId, openPositions: [] };
      accounts.push(account);
    }
    account.openPositions.push(rest);
    return accounts;
  }, []),
};

export const reporting: any = {
  getAccountTradeHistory: accountTradeHistory,
  getOpenPositionsData: openPositionsDataByAccount,
  getOrderDetailData: orderDetail,
};


// AMS

export const ams: any = {
  getAllMessages: allMessages,
  getMessage: message,
  getSummary: summary,
  voidResponse,
  notifyUnreadCountResponse: notifyUnreadCount,
};


/**
 * Mock mode for extended info. Populates data per case
 * @param caseNo Default is tradeuser logged in
 */
function individualExtendedInfo(caseNo?: ExtendedInfoMockEnum) {
  let res: TypedObject<IndividualExtendedInfoData | null>;
  switch (caseNo) {
    case ExtendedInfoMockEnum.ENROLL_1_1_PD:
      res = {
        individualExtendedInfo: individualExtendedInfo3 as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_1_2_PD:
      res = {
        individualExtendedInfo: individualExtendedInfo4 as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_2_1_FIN_QS:
      res = {
        individualExtendedInfo: individualExtendedInfo5 as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_2_4_SOF:
      res = {
        individualExtendedInfo: individualExtendedInfo6 as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_3_5_FIN_QS:
      res = {
        individualExtendedInfo: individualExtendedInfo1 as any,
        individualExtendedInfo_1: individualExtendedInfo2 as any,
        individualExtendedInfo_2: individualExtendedInfoNew as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_VERIFICATION:
      res = {
        individualExtendedInfo: individualExtendedInfo2 as any,
        individualExtendedInfo_2: individualExtendedInfoNew as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_DOCUMENT_VERIFICATION:
      res = {
        individualExtendedInfo: individualExtendedInfo7 as any,
      };
      break;

    case ExtendedInfoMockEnum.NULL:
      res = {
        individualExtendedInfo: null,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL:
      res = {
        individualExtendedInfo: {} as IndividualExtendedInfoData,
      };
      break;

    default:
      res = {
        individualExtendedInfo: individualExtendedInfoNew as any,
      };
      break;
  }

  res.individualExtendedInfo = {
    ...res.individualExtendedInfo,
    enroll_channel: configLib.isWeb ? 'W' : 'M',
  } as any;

  return res;
}
